import styled from "styled-components";

import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { MutatingDots } from "react-loader-spinner";
import { findTest } from "../../data/data";
import DisplayAdsStatic from "../../components/DisplayAdsStatic";
import { StoryPlayer } from "../../story/StoryPlayer";

const RESULT_DELAY_MS = 3000;

// 시작하기 → 스토리 테스트면 플레이어, 아니면 기존 문답
export const Test = () => {
  const { id } = useParams();
  const data = findTest(id);
  if (!data) return <Navigate to="/" replace />;
  if (data.kind === "story") return <StoryPlayer key={data.id} test={data} />;
  return <QuizTest key={data.id} data={data} />;
};

const QuizTest = ({ data }) => {
  const navigate = useNavigate();

  // 주제(subject)별 질문을 한 줄로 펼친 목록
  const questionData = useMemo(() => {
    if (!data) return [];
    return data.subjectQuestion.flatMap((item, subjectType) =>
      item.questions.map((question) => ({ ...question, subjectType }))
    );
  }, [data]);

  const subject = useRef([]);
  const [currentNumber, updateCurrentNumber] = useState(1);
  const [resultCount, setResultCount] = useState(null);

  // 테스트가 바뀌면 점수와 진행 상태를 초기화
  useEffect(() => {
    subject.current = data ? data.subjectQuestion.map(() => 0) : [];
    updateCurrentNumber(1);
    setResultCount(null);
    window.scrollTo(0, 0);
  }, [data]);

  // 결과가 정해지면 잠시 로딩을 보여준 뒤 결과 페이지로 이동
  useEffect(() => {
    if (resultCount === null) return;
    const timer = setTimeout(() => {
      navigate(`/main-test/${data.id}/result/${resultCount}`);
    }, RESULT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [resultCount, data, navigate]);

  const showLoading = resultCount !== null;
  const current = questionData[currentNumber - 1];

  const nextQuestion = (subjectType, value) => {
    // 마지막 답변 이후의 중복 클릭 무시
    if (showLoading) return;

    subject.current[subjectType] += value;
    if (currentNumber >= questionData.length) {
      setResultCount(
        parseInt(
          subject.current.map((item) => (item > 0 ? "1" : "0")).join(""),
          2
        )
      );
      return;
    }
    updateCurrentNumber(currentNumber + 1);
  };

  return (
    <TestContainer>
      <ProgressContainer>
        <ProgressBar
          style={{ width: `${(currentNumber / questionData.length) * 100}%` }}
        />
      </ProgressContainer>
      <QuestionContainer>
        <Question>{current.content}</Question>
        {current.answer.map((item, index) => (
          <QuestionAnswer
            key={`${current.no}-${index}`}
            onClick={() => nextQuestion(current.subjectType, item.value)}
          >
            {item.contents}
          </QuestionAnswer>
        ))}
      </QuestionContainer>
      <DisplayAdsStatic />
      {showLoading && (
        <Dimd>
          <MutatingDots color="#FFFFFF" />
          <LoadingText>잠시만 기다려주세요.</LoadingText>
        </Dimd>
      )}
    </TestContainer>
  );
};

const TestContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ProgressContainer = styled.div`
  position: relative;
  width: 100%;
  height: 20px;

  background-color: #ccc;

  border-radius: 10px;
  overflow: hidden;
`;

const ProgressBar = styled.div`
  position: absolute;
  width: 0;
  height: 100%;
  left: 0;
  top: 0;
  background-color: #333;
  transition: width 0.2s ease;
`;

const QuestionContainer = styled.div`
  display: flex;
  flex-direction: column;

  margin-bottom: 24px;
`;

const Question = styled.pre`
  text-align: center;
  font-family: "GyeonggiBatang";
  font-size: 16px;
  font-weight: 400;
  margin: 0;
  margin-top: 40px;
  margin-bottom: 24px;
  letter-spacing: 0.7px;
  line-height: 1.2;
`;

const QuestionAnswer = styled.pre`
  min-height: 74px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  text-align: center;
  margin: 0;
  margin-top: 16px;

  font-family: "GyeonggiBatang";
  font-size: 14px;
  line-height: 20px;
  font-weight: 200;

  border-radius: 24px;
  border: 1px solid rgba(209, 213, 219, 1);
  transition: all 0.2s ease;

  cursor: pointer;

  user-select: none;

  &:hover {
    background-color: #ececec;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const Dimd = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  min-height: 100vh;
  margin: 0;
  padding: 0;
  background-color: rgba(0, 0, 0, 1);
  z-index: 10;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  font-size: 20px;
  font-weight: 300;
  color: white;
`;

// 이전에는 전역 <style>로 모든 div에 적용하던 스타일을 이 문구에만 적용
const LoadingText = styled.div`
  color: white;
  padding: 10px;
`;
